/**
 * Conferência das respostas da API de violações, como o `client/api.py` do painel antigo
 * fazia com o Pydantic (`TypeAdapter(list[X]).validate_python(r.json())` ou
 * `X.model_validate(r.json())`).
 *
 * Quando a resposta vem num formato inválido, a função lança um erro com o mesmo texto do
 * `ValidationError` do Pydantic (ex.: "1 validation error for ViolationTimeSeries ..."),
 * e a página mostra o aviso em vez de quebrar. Só cobre os tipos usados nos modelos de
 * violações, no modo flexível ("lax") do Pydantic.
 */
import { apiFetchTexto, type OpcoesRequisicao } from '@/lib/api'

import { lerJsonPython, NumeroPython } from '@/api/violacoes-json'

/** Versão do Pydantic do painel antigo (aparece no link das mensagens). */
const VERSAO_PYDANTIC = '2.13'

// ── Leitura do JSON (como o `r.json()` do httpx) ─────────────────────────

/** Chama a API e lê o corpo como JSON; corpo que não é JSON vira erro (igual ao Python). */
async function buscarJson(caminho: string, opcoes?: OpcoesRequisicao): Promise<unknown> {
  return lerJsonPython(await apiFetchTexto(caminho, opcoes))
}

// ── Inteiros grandes ──────────────────────────────────────────────────────

/**
 * Texto exato dos inteiros grandes demais para o JavaScript (acima de 2^53), guardado pelo
 * valor aproximado. O Python mostra esses números sem perder dígitos.
 */
const textosExatos = new Map<number, string>()

/** Texto do inteiro como o Python mostraria (sem perder dígitos de números enormes). */
export function textoInteiro(n: number): string {
  if (Number.isSafeInteger(n)) return String(n)
  return textosExatos.get(n) ?? BigInt(Math.trunc(n)).toString()
}

/** Soma exata de inteiros, em texto (como a soma de `int` do Python). */
export function somaInteiros(valores: number[]): string {
  return valores.reduce((total, n) => total + BigInt(textoInteiro(n)), 0n).toString()
}

function inteiroExato(texto: string): number {
  const valor = Number(texto)
  if (!Number.isSafeInteger(valor)) textosExatos.set(valor, texto)
  return valor === 0 ? 0 : valor
}

// ── repr() do Python e tipo da entrada ────────────────────────────────────

/** Caracteres que o Python não considera imprimíveis (`str.isprintable()`). */
const NAO_IMPRIMIVEL = /^[\p{Cc}\p{Cf}\p{Cs}\p{Co}\p{Cn}\p{Zl}\p{Zp}\p{Zs}]$/u

function reprTexto(texto: string): string {
  const aspas = texto.includes("'") && !texto.includes('"') ? '"' : "'"
  let saida = ''
  for (const caractere of texto) {
    const codigo = caractere.codePointAt(0) ?? 0
    if (caractere === '\\') saida += '\\\\'
    else if (caractere === aspas) saida += `\\${aspas}`
    else if (caractere === '\n') saida += '\\n'
    else if (caractere === '\r') saida += '\\r'
    else if (caractere === '\t') saida += '\\t'
    else if (caractere !== ' ' && NAO_IMPRIMIVEL.test(caractere)) {
      // Mesmo escape do `repr()` do Python para caracteres não imprimíveis.
      if (codigo < 0x100) saida += `\\x${codigo.toString(16).padStart(2, '0')}`
      else if (codigo < 0x10000) saida += `\\u${codigo.toString(16).padStart(4, '0')}`
      else saida += `\\U${codigo.toString(16).padStart(8, '0')}`
    } else saida += caractere
  }
  return `${aspas}${saida}${aspas}`
}

function reprNumero(valor: number): string {
  if (Number.isInteger(valor) && Math.abs(valor) < 1e16) return String(valor)
  return String(valor).replace(/e([+-])(\d)$/, 'e$10$2')
}

/** `repr()` de um `float` do Python (ex.: `1.0`, `1e+20`, `1.5e-07`, `inf`, `nan`). */
function reprFlutuante(valor: number): string {
  if (Number.isNaN(valor)) return 'nan'
  if (!Number.isFinite(valor)) return valor < 0 ? '-inf' : 'inf'
  if (valor === 0) return Object.is(valor, -0) ? '-0.0' : '0.0'
  const sinal = valor < 0 ? '-' : ''
  const [mantissa = '', expoenteTexto = '0'] = Math.abs(valor).toExponential().split('e')
  const digitos = mantissa.replace('.', '')
  const expoente = Number(expoenteTexto)
  if (expoente >= -4 && expoente < 16) {
    if (expoente < 0) return `${sinal}0.${'0'.repeat(-expoente - 1)}${digitos}`
    if (digitos.length <= expoente + 1) return `${sinal}${digitos.padEnd(expoente + 1, '0')}.0`
    return `${sinal}${digitos.slice(0, expoente + 1)}.${digitos.slice(expoente + 1)}`
  }
  const expoenteSaida = `${expoente < 0 ? '-' : '+'}${String(Math.abs(expoente)).padStart(2, '0')}`
  return `${sinal}${mantissa}e${expoenteSaida}`
}

/** `repr()` do Python para valores vindos de JSON. */
function reprPython(valor: unknown): string {
  if (valor === null || valor === undefined) return 'None'
  if (valor === true) return 'True'
  if (valor === false) return 'False'
  if (typeof valor === 'string') return reprTexto(valor)
  if (typeof valor === 'number') return reprNumero(valor)
  if (valor instanceof NumeroPython) {
    return valor.flutuante ? reprFlutuante(valor.valor) : valor.texto
  }
  if (Array.isArray(valor)) return `[${valor.map(reprPython).join(', ')}]`
  if (typeof valor === 'object') {
    const itens = Object.entries(valor as Record<string, unknown>).map(
      ([chave, v]) => `${reprTexto(chave)}: ${reprPython(v)}`
    )
    return `{${itens.join(', ')}}`
  }
  return ''
}

/**
 * `input_value` do Pydantic: o repr, cortado no meio quando passa de 50 bytes (UTF-8).
 * O começo fica com até 25 bytes e o fim com até 24, sem partir caracteres.
 */
function valorDeEntrada(valor: unknown): string {
  const repr = reprPython(valor)
  const bytes = (t: string) => new TextEncoder().encode(t).length
  const total = bytes(repr)
  if (total <= 50) return repr
  let inicio = ''
  let fim = ''
  let contados = 0
  for (const caractere of repr) {
    const tamanho = bytes(caractere)
    if (contados + tamanho > 25) break
    inicio += caractere
    contados += tamanho
  }
  contados = 0
  for (const caractere of Array.from(repr).reverse()) {
    const tamanho = bytes(caractere)
    if (contados + tamanho > 24) break
    fim = caractere + fim
    contados += tamanho
  }
  return `${inicio}...${fim}`
}

/** Nome do tipo no Python (`input_type`). */
function tipoPython(valor: unknown): string {
  if (valor === null || valor === undefined) return 'NoneType'
  if (typeof valor === 'boolean') return 'bool'
  if (typeof valor === 'number') return Number.isInteger(valor) ? 'int' : 'float'
  if (valor instanceof NumeroPython) return valor.flutuante ? 'float' : 'int'
  if (typeof valor === 'string') return 'str'
  if (Array.isArray(valor)) return 'list'
  return 'dict'
}

// ── Validadores de cada tipo ──────────────────────────────────────────────

type Caminho = (string | number)[]

interface ErroCampo {
  caminho: Caminho
  tipo: string
  mensagem: string
  valor: unknown
}

type Validador = (valor: unknown, caminho: Caminho, erros: ErroCampo[]) => unknown

const ehObjeto = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v) && !(v instanceof NumeroPython)

function erro(
  erros: ErroCampo[],
  caminho: Caminho,
  tipo: string,
  mensagem: string,
  valor: unknown
): undefined {
  erros.push({ caminho, tipo, mensagem, valor })
  return undefined
}

/** `int`: aceita bool, número sem parte decimal e texto com número inteiro. */
function validarInteiro(v: unknown, caminho: Caminho, erros: ErroCampo[]): number | undefined {
  if (typeof v === 'boolean') return v ? 1 : 0
  if (v instanceof NumeroPython) {
    if (!v.flutuante) return inteiroExato(v.texto)
    if (!Number.isFinite(v.valor)) {
      return erro(erros, caminho, 'finite_number', 'Input should be a finite number', v)
    }
    if (!Number.isInteger(v.valor)) {
      return erro(
        erros,
        caminho,
        'int_from_float',
        'Input should be a valid integer, got a number with a fractional part',
        v
      )
    }
    // Número com casas decimais fora da faixa de 64 bits não vira inteiro no Pydantic.
    if (Math.abs(v.valor) >= 2 ** 63) {
      return erro(
        erros,
        caminho,
        'int_parsing_size',
        'Unable to parse input string as an integer, exceeded maximum size',
        v
      )
    }
    return v.valor === 0 ? 0 : v.valor
  }
  if (typeof v === 'number') {
    if (Number.isInteger(v)) return v
    return erro(
      erros,
      caminho,
      'int_from_float',
      'Input should be a valid integer, got a number with a fractional part',
      v
    )
  }
  if (typeof v === 'string') {
    const numero = /^([+-]?\d+)(\.0*)?$/.exec(v.trim().replace(/_/g, ''))
    if (numero?.[1]) return inteiroExato(BigInt(numero[1]).toString())
    return erro(
      erros,
      caminho,
      'int_parsing',
      'Input should be a valid integer, unable to parse string as an integer',
      v
    )
  }
  return erro(erros, caminho, 'int_type', 'Input should be a valid integer', v)
}

const inteiro: Validador = validarInteiro

/** `int = Field(ge=0)`. */
const inteiroNaoNegativo: Validador = (v, caminho, erros) => {
  const n = validarInteiro(v, caminho, erros)
  if (n === undefined) return undefined
  if (n < 0) {
    return erro(
      erros,
      caminho,
      'greater_than_equal',
      'Input should be greater than or equal to 0',
      v
    )
  }
  return n
}

/** `str`: só aceita texto. */
const texto: Validador = (v, caminho, erros) => {
  if (typeof v === 'string') return v
  return erro(erros, caminho, 'string_type', 'Input should be a valid string', v)
}

/** `X | None`. */
function opcional(validar: Validador): Validador {
  return (v, caminho, erros) => (v === null ? null : validar(v, caminho, erros))
}

/** `Literal["a", "b"]`. */
function literal(...opcoes: string[]): Validador {
  const nomes = opcoes.map((o) => `'${o}'`)
  const lista =
    nomes.length > 1 ? `${nomes.slice(0, -1).join(', ')} or ${nomes[nomes.length - 1]}` : nomes[0]
  return (v, caminho, erros) => {
    if (typeof v === 'string' && opcoes.includes(v)) return v
    return erro(erros, caminho, 'literal_error', `Input should be ${lista}`, v)
  }
}

/** `list[X]`. */
function lista(validar: Validador): Validador {
  return (v, caminho, erros) => {
    if (!Array.isArray(v)) {
      return erro(erros, caminho, 'list_type', 'Input should be a valid list', v)
    }
    return (v as unknown[]).map((item, i) => validar(item, [...caminho, i], erros))
  }
}

// ── Datas (mesmas regras do `speedate`, usado pelo Pydantic) ───────────────

interface DataHora {
  ano: number
  mes: number
  dia: number
  hora: number
  minuto: number
  segundo: number
  fracao: number
  /** Fuso em minutos (`null` = sem fuso, "naive" no Python). */
  fuso?: number | null
}

class ErroData extends Error {}

const ehDigito = (c: string | undefined) => c !== undefined && c >= '0' && c <= '9'

function diasNoMes(ano: number, mes: number): number {
  if (mes === 2) return (ano % 4 === 0 && ano % 100 !== 0) || ano % 400 === 0 ? 29 : 28
  return [4, 6, 9, 11].includes(mes) ? 30 : 31
}

/** Lê `AAAA-MM-DD` do começo do texto (o resto é tratado por quem chama). */
function lerDataParcial(t: string): { ano: number; mes: number; dia: number } {
  if (t.length < 10) throw new ErroData('input is too short')
  const digitos = (inicio: number, fim: number, mensagem: string) => {
    let n = 0
    for (let i = inicio; i < fim; i++) {
      if (!ehDigito(t[i])) throw new ErroData(mensagem)
      n = n * 10 + Number(t[i])
    }
    return n
  }
  const ano = digitos(0, 4, 'invalid character in year')
  if (t[4] !== '-') throw new ErroData('invalid date separator, expected `-`')
  const mes = digitos(5, 7, 'invalid character in month')
  if (t[7] !== '-') throw new ErroData('invalid date separator, expected `-`')
  const dia = digitos(8, 10, 'invalid character in day')
  if (mes < 1 || mes > 12) throw new ErroData('month value is outside expected range of 1-12')
  if (dia < 1 || dia > diasNoMes(ano, mes)) {
    throw new ErroData('day value is outside expected range')
  }
  return { ano, mes, dia }
}

/** Data pura (`AAAA-MM-DD`, sem nada depois). */
function lerData(t: string): DataHora {
  const d = lerDataParcial(t)
  if (t.length > 10) throw new ErroData('unexpected extra characters at the end of the input')
  return { ...d, hora: 0, minuto: 0, segundo: 0, fracao: 0 }
}

/** Data e hora (`AAAA-MM-DDTHH:MM[:SS[.ffffff]][Z|±HH[:]MM]`). */
function lerDataHora(t: string): DataHora {
  const d = lerDataParcial(t)
  if (!['T', 't', ' ', '_'].includes(t[10] ?? '')) {
    throw new ErroData('invalid datetime separator, expected `T`, `t`, `_` or space')
  }
  if (t.length - 11 < 5) throw new ErroData('input is too short')
  const doisDigitos = (i: number, mensagem: string) => {
    if (!ehDigito(t[i]) || !ehDigito(t[i + 1])) throw new ErroData(mensagem)
    return Number(t[i]) * 10 + Number(t[i + 1])
  }
  const hora = doisDigitos(11, 'invalid character in hour')
  if (t[13] !== ':') throw new ErroData('invalid time separator, expected `:`')
  const minuto = doisDigitos(14, 'invalid character in minute')
  let pos = 16
  let segundo = 0
  let fracao = 0
  if (t[pos] === ':') {
    segundo = doisDigitos(pos + 1, 'invalid character in second')
    pos += 3
    if (t[pos] === '.' || t[pos] === ',') {
      pos += 1
      const inicio = pos
      while (ehDigito(t[pos])) pos += 1
      if (pos === inicio) throw new ErroData('second fraction digits missing after `.`')
      fracao = Number(`0.${t.slice(inicio, pos)}`)
    }
  }
  if (hora > 23) throw new ErroData('hour value is outside expected range of 0-23')
  if (minuto > 59) throw new ErroData('minute value is outside expected range of 0-59')
  if (segundo > 59) throw new ErroData('second value is outside expected range of 0-59')
  const c = t[pos]
  let fuso: number | null = null
  if (c === 'Z' || c === 'z') {
    pos += 1
    fuso = 0
  } else if (c === '+' || c === '-') {
    // O fuso precisa de horas e minutos (`+03` sozinho é recusado, como no `speedate`).
    const hTz = doisDigitos(pos + 1, 'invalid timezone hour')
    pos += 3
    if (t[pos] === ':') pos += 1
    const mTz = doisDigitos(pos, 'invalid timezone minute')
    pos += 2
    if (hTz * 60 + mTz >= 24 * 60) {
      throw new ErroData('timezone offset must be less than 24 hours')
    }
    fuso = (c === '-' ? -1 : 1) * (hTz * 60 + mTz)
  } else if (c !== undefined) throw new ErroData('invalid timezone sign')
  if (pos < t.length) throw new ErroData('unexpected extra characters at the end of the input')
  return { ...d, hora, minuto, segundo, fracao, fuso }
}

/** Número como horário Unix (segundos, ou milissegundos quando passa de 2e10). */
function lerTimestamp(n: number): DataHora {
  if (Number.isNaN(n)) throw new ErroData('NaN values not permitted')
  const segundos = Math.abs(n) > 2e10 ? n / 1000 : n
  if (segundos > 253_402_300_799) {
    throw new ErroData('dates after 9999 are not supported as unix timestamps')
  }
  if (segundos < -62_135_596_800) {
    throw new ErroData('dates before 0000 are not supported as unix timestamps')
  }
  const inteiros = Math.floor(segundos)
  const d = new Date(inteiros * 1000)
  return {
    ano: d.getUTCFullYear(),
    mes: d.getUTCMonth() + 1,
    dia: d.getUTCDate(),
    hora: d.getUTCHours(),
    minuto: d.getUTCMinutes(),
    segundo: d.getUTCSeconds(),
    fracao: segundos - inteiros,
    fuso: 0,
  }
}

/** Texto que é só número vira horário Unix (como o `speedate`). */
function numeroDoTexto(t: string): number | null {
  return /^[+-]?\d+(\.\d+)?$/.test(t) ? Number(t) : null
}

const doisDigitosTexto = (n: number) => String(n).padStart(2, '0')

function dataIso(d: DataHora): string {
  return `${String(d.ano).padStart(4, '0')}-${doisDigitosTexto(d.mes)}-${doisDigitosTexto(d.dia)}`
}

const horaZerada = (d: DataHora) =>
  d.hora === 0 && d.minuto === 0 && d.segundo === 0 && d.fracao === 0

/** Valor do número do JSON (inteiro comum ou `NumeroPython`); `null` quando não é número. */
function valorNumerico(v: unknown): number | null {
  if (typeof v === 'number') return v
  return v instanceof NumeroPython ? v.valor : null
}

/** `date`: devolve `AAAA-MM-DD`. */
const data: Validador = (v, caminho, erros) => {
  const inexata = () =>
    erro(
      erros,
      caminho,
      'date_from_datetime_inexact',
      'Datetimes provided to dates should have zero time - e.g. be exact dates',
      v
    )
  const falha = (e: unknown) => {
    if (!(e instanceof ErroData)) throw e
    return erro(
      erros,
      caminho,
      'date_from_datetime_parsing',
      `Input should be a valid date or datetime, ${e.message}`,
      v
    )
  }
  const numero = valorNumerico(v)
  if (numero !== null) {
    try {
      const d = lerTimestamp(numero)
      return horaZerada(d) ? dataIso(d) : inexata()
    } catch (e) {
      return falha(e)
    }
  }
  if (typeof v !== 'string') {
    return erro(erros, caminho, 'date_type', 'Input should be a valid date', v)
  }
  try {
    return dataIso(lerData(v))
  } catch {
    // Não é data pura: tenta como horário Unix e depois como data e hora.
  }
  try {
    const n = numeroDoTexto(v)
    const d = n !== null ? lerTimestamp(n) : lerDataHora(v)
    return horaZerada(d) ? dataIso(d) : inexata()
  } catch (e) {
    return falha(e)
  }
}

/**
 * Texto ISO padronizado (`AAAA-MM-DDTHH:MM:SS.ffffff` + fuso, sem fuso quando "naive"),
 * que o `new Date` do navegador sempre entende. Assim a página formata qualquer data
 * aceita pelo Pydantic (horário Unix, `z` minúsculo, fração com vírgula etc.).
 */
function dataHoraIso(d: DataHora): string {
  const micro = String(Math.min(999_999, Math.floor(d.fracao * 1_000_000 + 1e-6))).padStart(6, '0')
  const hora = `${doisDigitosTexto(d.hora)}:${doisDigitosTexto(d.minuto)}:${doisDigitosTexto(d.segundo)}`
  let fuso = ''
  if (d.fuso !== null && d.fuso !== undefined) {
    const total = Math.abs(d.fuso)
    const sinal = d.fuso < 0 ? '-' : '+'
    fuso = `${sinal}${doisDigitosTexto(Math.floor(total / 60))}:${doisDigitosTexto(total % 60)}`
  }
  return `${dataIso(d)}T${hora}.${micro}${fuso}`
}

/** `datetime`: devolve a data em texto ISO padronizado (a página formata a data depois). */
const dataHora: Validador = (v, caminho, erros) => {
  const numero = valorNumerico(v)
  if (numero !== null) {
    try {
      return dataHoraIso(lerTimestamp(numero))
    } catch (e) {
      if (!(e instanceof ErroData)) throw e
      return erro(
        erros,
        caminho,
        'datetime_parsing',
        `Input should be a valid datetime, ${e.message}`,
        v
      )
    }
  }
  if (typeof v !== 'string') {
    return erro(erros, caminho, 'datetime_type', 'Input should be a valid datetime', v)
  }
  try {
    return dataHoraIso(lerDataHora(v))
  } catch {
    // Não é data e hora: tenta como horário Unix e depois como data pura.
  }
  try {
    const n = numeroDoTexto(v)
    return dataHoraIso(n !== null ? lerTimestamp(n) : lerData(v))
  } catch (e) {
    if (!(e instanceof ErroData)) throw e
    return erro(
      erros,
      caminho,
      'datetime_from_date_parsing',
      `Input should be a valid datetime or date, ${e.message}`,
      v
    )
  }
}

// ── Modelos ───────────────────────────────────────────────────────────────

/** Campo do modelo: nome, validador e se tem valor padrão (padrão sempre `None`). */
type Campo = [nome: string, validar: Validador, temPadrao?: boolean]

/**
 * Modelo Pydantic. Com `from_attributes`, a mensagem para entrada que não é objeto muda
 * (é a do `model_attributes_type`).
 */
function modelo(nome: string, campos: Campo[], deAtributos = false): Validador {
  return (v, caminho, erros) => {
    if (!ehObjeto(v)) {
      return deAtributos
        ? erro(
            erros,
            caminho,
            'model_attributes_type',
            'Input should be a valid dictionary or object to extract fields from',
            v
          )
        : erro(
            erros,
            caminho,
            'model_type',
            `Input should be a valid dictionary or instance of ${nome}`,
            v
          )
    }
    const saida: Record<string, unknown> = {}
    for (const [campo, validar, temPadrao] of campos) {
      if (!Object.hasOwn(v, campo)) {
        if (temPadrao) saida[campo] = null
        else erro(erros, [...caminho, campo], 'missing', 'Field required', v)
        continue
      }
      saida[campo] = validar(v[campo], [...caminho, campo], erros)
    }
    return saida
  }
}

const ViolationResponse = modelo(
  'ViolationResponse',
  [
    ['id', inteiro],
    ['gitlab_project_id', inteiro],
    ['project_path_with_namespace', opcional(texto), true],
    ['project_name', opcional(texto), true],
    ['gitlab_group_id', opcional(inteiro), true],
    ['resource_type', texto],
    ['resource_iid', inteiro],
    ['rule_catalog_id', inteiro],
    ['rule_code', texto],
    ['author_username', texto],
    ['detected_at', dataHora],
  ],
  true
)

const ViolationCountByRule = modelo('ViolationCountByRule', [
  ['rule_code', texto],
  ['rule_name', texto],
  ['rule_category', texto],
  ['count', inteiroNaoNegativo],
])

const ViolationCountByCategory = modelo('ViolationCountByCategory', [
  ['category', texto],
  ['count', inteiroNaoNegativo],
])

const ViolationCountByProject = modelo('ViolationCountByProject', [
  ['project_path_with_namespace', opcional(texto)],
  ['project_name', opcional(texto)],
  ['count', inteiroNaoNegativo],
])

const granularidade = literal('day', 'week')

const ViolationTimeSeries = modelo('ViolationTimeSeries', [
  ['granularity', granularidade],
  [
    'series',
    lista(
      modelo('ViolationTimePoint', [
        ['period', data],
        ['count', inteiroNaoNegativo],
      ])
    ),
  ],
])

const WebhookEventTimeSeries = modelo('WebhookEventTimeSeries', [
  ['granularity', granularidade],
  [
    'series',
    lista(
      modelo('WebhookEventTimePoint', [
        ['period', data],
        ['events_count', inteiroNaoNegativo],
        ['events_with_violations', inteiroNaoNegativo],
        ['events_without_violations', inteiroNaoNegativo],
      ])
    ),
  ],
])

const ScopeSearchItem = modelo('ScopeSearchItem', [
  ['entity_type', literal('project', 'group')],
  ['full_path', texto],
  ['name', texto],
  ['parent_group_path', opcional(texto), true],
  ['gitlab_project_id', opcional(inteiro), true],
  ['count', inteiroNaoNegativo],
])

const ViolationAuthorSearchItem = modelo('ViolationAuthorSearchItem', [
  ['author_username', texto],
  ['count', inteiroNaoNegativo],
])

const ViolationCountByAuthorRule = modelo('ViolationCountByAuthorRule', [
  ['author_username', texto],
  ['rule_code', texto],
  ['count', inteiroNaoNegativo],
])

/** Validadores de cada resposta, com o nome que aparece na mensagem do Pydantic. */
export const MODELOS = {
  violacoes: ['list[ViolationResponse]', lista(ViolationResponse)],
  porRegra: ['list[ViolationCountByRule]', lista(ViolationCountByRule)],
  porCategoria: ['list[ViolationCountByCategory]', lista(ViolationCountByCategory)],
  porProjeto: ['list[ViolationCountByProject]', lista(ViolationCountByProject)],
  serie: ['ViolationTimeSeries', ViolationTimeSeries],
  serieWebhooks: ['WebhookEventTimeSeries', WebhookEventTimeSeries],
  escopos: ['list[ScopeSearchItem]', lista(ScopeSearchItem)],
  autores: ['list[ViolationAuthorSearchItem]', lista(ViolationAuthorSearchItem)],
  porAutorRegra: ['list[ViolationCountByAuthorRule]', lista(ViolationCountByAuthorRule)],
} as const satisfies Record<string, readonly [string, Validador]>

export type NomeModelo = keyof typeof MODELOS

/** Confere os dados com o modelo; formato inválido vira o texto do `ValidationError`. */
export function validarResposta<T>(modelo: NomeModelo, dados: unknown): T {
  const [titulo, validar] = MODELOS[modelo]
  const erros: ErroCampo[] = []
  const saida = validar(dados, [], erros)
  if (erros.length) {
    const cabecalho = `${erros.length} validation error${erros.length === 1 ? '' : 's'} for ${titulo}`
    const linhas = erros.map((e) => {
      const local = e.caminho.length ? `${e.caminho.join('.')}\n` : ''
      return (
        `${local}  ${e.mensagem} [type=${e.tipo}, input_value=${valorDeEntrada(e.valor)}, input_type=${tipoPython(e.valor)}]\n` +
        `    For further information visit https://errors.pydantic.dev/${VERSAO_PYDANTIC}/v/${e.tipo}`
      )
    })
    throw new Error([cabecalho, ...linhas].join('\n'))
  }
  return saida as T
}

/** `GET` na API, lendo o JSON e conferindo o formato como o `client/api.py`. */
export async function buscarValidado<T>(
  modelo: NomeModelo,
  caminho: string,
  opcoes?: OpcoesRequisicao
): Promise<T> {
  return validarResposta<T>(modelo, await buscarJson(caminho, opcoes))
}
